import { useMediaQuery, useTheme } from "@mui/material";

/**
 * H3 · The ONE breakpoint set.
 *
 * Before this hook the app carried three different mobile cutoffs — 600 (`sm`),
 * 900 (`md`) and a hand-rolled 960 — and the same page could be "mobile" for its
 * table swap, "desktop" for its header labels and "mobile" again for its dialogs.
 * The mobile-readiness audit traced real data loss to that split: Billing
 * Invoices renders its totals at `md` (900) and its popover fallback at `sm`
 * (600), so between 600 and 899 — which is exactly where an iPad in portrait
 * sits at 820 CSS px — NEITHER rendered and the figures existed nowhere.
 *
 * The set, and what each band is for:
 *
 *  - `isPhone`   <600   — one column. Tables become card lists here and ONLY here.
 *  - `isTablet`  600–899 — an iPad portrait (820). Wide enough for a real table;
 *                          card-swapping it wastes half the screen. Still touch,
 *                          so the 44px floor and full-screen dialogs still apply.
 *  - `isDesktop` >=900  — pointer input, hover is a real affordance.
 *
 * `isTouch` (= `isPhone || isTablet`, i.e. <900) is the band for anything driven
 * by *finger* rather than *width*: touch-target sizing, full-screen dialogs, and
 * "this is hover-only, so it does not exist" fixes. Use `isPhone` for layout,
 * `isTouch` for interaction. That distinction is the whole point of the split —
 * the old single `isMobile` conflated them and every page had to pick one.
 */
export interface Responsive {
  /** <600px. The only band that should swap a table for cards. */
  isPhone: boolean;
  /** 600-899px — iPad portrait. Table-capable, but still a finger. */
  isTablet: boolean;
  /** >=900px. */
  isDesktop: boolean;
  /** <900px — phone or tablet. Sizing/affordance rules key on this. */
  isTouch: boolean;
}

/** Numeric edges, exported so non-React code (route configs, tests) agrees. */
export const PHONE_MAX = 600;
export const TABLET_MAX = 900;

export function useResponsive(): Responsive {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const isTouch = useMediaQuery(theme.breakpoints.down("md"));
  return {
    isPhone,
    isTablet: isTouch && !isPhone,
    isDesktop: !isTouch,
    isTouch,
  };
}

export default useResponsive;
